import { useEffect, useRef, useState } from "react";
import { useAuthController, useDataSource } from "@firecms/core";
import type { AuthExtras, WorkspaceAuthScope } from "../types/auth";
import {
  verifyPanelAccess,
  type PanelAccessOutcome,
  type PanelAccessResult,
} from "../custom/panelAccessResolve";
import type { PanelAccessDenialReason } from "../workspace/eventAccessMessages";
import {
  canAccessMode,
  getWorkspaceKey,
  type AdminMode,
} from "../workspace/adminMode";

type PanelGatePhase = "idle" | "checking" | "granted" | "denied";

const isGuardedMode = (mode: AdminMode): boolean =>
  mode.kind === "global" ||
  mode.kind === "account" ||
  mode.kind === "space" ||
  mode.kind === "event";

type CachedPanelAccess = {
  outcome: PanelAccessOutcome;
  denialMessage?: string;
  denialReason?: PanelAccessDenialReason;
  workspace?: WorkspaceAuthScope;
};

const panelAccessCache = new Map<string, CachedPanelAccess>();

const panelAccessCacheKey = (userId: string, modeKey: string): string =>
  `${userId}:${modeKey}`;

const readCachedAccess = (
  userId: string | undefined,
  modeKey: string
): CachedPanelAccess | undefined => {
  if (!userId || !modeKey) {
    return undefined;
  }
  return panelAccessCache.get(panelAccessCacheKey(userId, modeKey));
};

const writeCachedAccess = (
  userId: string,
  modeKey: string,
  result: PanelAccessResult
): void => {
  panelAccessCache.set(panelAccessCacheKey(userId, modeKey), result);
};

const initialPhaseForWorkspace = (
  userId: string | undefined,
  modeKey: string,
  needsGate: boolean
): PanelGatePhase => {
  if (!needsGate || !userId) {
    return "idle";
  }
  const cached = readCachedAccess(userId, modeKey);
  if (cached?.outcome === "granted" || cached?.outcome === "denied") {
    return cached.outcome;
  }
  return "idle";
};

const applyWorkspace = (
  authController: {
    extra: unknown;
    setExtra: (extra: AuthExtras) => void;
  },
  workspace: WorkspaceAuthScope | undefined
): void => {
  const current = authController.extra as AuthExtras | undefined;
  if (!current) {
    return;
  }
  if (current.workspace === workspace) {
    return;
  }
  if (
    current.workspace?.accountId === workspace?.accountId &&
    current.workspace?.eventId === workspace?.eventId &&
    current.workspace?.isAccountManager === workspace?.isAccountManager &&
    current.workspace?.isEventManager === workspace?.isEventManager
  ) {
    return;
  }
  authController.setExtra({ ...current, workspace });
};

export function usePanelExtras(
  mode: AdminMode,
  enabled = true
): {
  loading: boolean;
  accessDenied: boolean;
  denialMessage?: string;
  denialReason?: PanelAccessDenialReason;
} {
  const authController = useAuthController();
  const dataSource = useDataSource();
  const modeKey = getWorkspaceKey(mode);
  const userId = authController.user?.uid;
  const extra = authController.extra as AuthExtras | undefined;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const isSuperManager = extra?.isSuperManager === true;
  const modeAllowed = !!extra && canAccessMode(mode, extra);
  const needsGate = enabled && !!userId && !!extra && isGuardedMode(mode);

  const [phase, setPhase] = useState<PanelGatePhase>(() =>
    initialPhaseForWorkspace(userId, modeKey, needsGate)
  );
  const [denialMessage, setDenialMessage] = useState<string | undefined>(
    () => readCachedAccess(userId, modeKey)?.denialMessage
  );
  const [denialReason, setDenialReason] = useState<
    PanelAccessDenialReason | undefined
  >(() => readCachedAccess(userId, modeKey)?.denialReason);

  const prevGateKeyRef = useRef<string | null>(null);
  const gateKey = userId && modeKey ? `${userId}:${modeKey}` : null;

  useEffect(() => {
    if (gateKey !== prevGateKeyRef.current) {
      prevGateKeyRef.current = gateKey;
      setPhase(initialPhaseForWorkspace(userId, modeKey, needsGate));
    }
  }, [gateKey, modeKey, needsGate, userId]);

  // Clear workspace when leaving guarded modes (e.g. hub).
  useEffect(() => {
    if (!isGuardedMode(mode)) {
      applyWorkspace(authController, undefined);
    }
  }, [authController, mode]);

  useEffect(() => {
    const currentMode = modeRef.current;

    if (!enabled || !userId || !extra || !isGuardedMode(currentMode)) {
      return;
    }

    if (!modeAllowed) {
      applyWorkspace(authController, undefined);
      setPhase("denied");
      return;
    }

    const applyGranted = (workspace: WorkspaceAuthScope | undefined) => {
      applyWorkspace(authController, workspace);
      setDenialMessage(undefined);
      setDenialReason(undefined);
      setPhase("granted");
    };

    const cached = readCachedAccess(userId, modeKey);
    if (cached?.outcome === "granted") {
      applyGranted(cached.workspace);
      return;
    }

    if (currentMode.kind === "global") {
      const result: PanelAccessResult = isSuperManager
        ? { outcome: "granted" }
        : { outcome: "denied" };
      writeCachedAccess(userId, modeKey, result);
      if (result.outcome === "denied") {
        applyWorkspace(authController, undefined);
      }
      setDenialMessage(result.denialMessage);
      setDenialReason(result.denialReason);
      setPhase(result.outcome);
      return;
    }

    if (isSuperManager && currentMode.kind !== "event") {
      const workspace: WorkspaceAuthScope = {
        accountId: currentMode.id.accountId,
        isAccountManager: true,
        isEventManager: false,
      };
      const result: PanelAccessResult = { outcome: "granted", workspace };
      writeCachedAccess(userId, modeKey, result);
      applyGranted(workspace);
      return;
    }

    let cancelled = false;
    setPhase("checking");
    applyWorkspace(authController, undefined);

    void verifyPanelAccess(dataSource, userId, currentMode, extra).then(
      (result) => {
        if (cancelled) {
          return;
        }
        if (result.outcome === "granted" || !result.denialMessage) {
          writeCachedAccess(userId, modeKey, result);
        }
        if (result.outcome === "granted") {
          applyGranted(result.workspace);
          return;
        }
        applyWorkspace(authController, undefined);
        setDenialMessage(result.denialMessage);
        setDenialReason(result.denialReason);
        setPhase("denied");
      }
    );

    return () => {
      cancelled = true;
    };
  }, [
    authController,
    dataSource,
    enabled,
    isSuperManager,
    modeAllowed,
    modeKey,
    userId,
  ]);

  const loading = needsGate && phase === "checking";
  const accessDenied =
    needsGate &&
    (phase === "denied" || (!modeAllowed && phase !== "checking"));

  return {
    loading,
    accessDenied,
    denialMessage: accessDenied ? denialMessage : undefined,
    denialReason: accessDenied ? denialReason : undefined,
  };
}
